import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useProduct } from '../../bff-api/products/useProduct';
import { useSSRProduct } from '../../contexts/pdp/useSSRProduct';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { usePhotoGridProductId, usePhotoGridQueryKey } from './usePhotoGridProductId';
import { buildAssets } from './utils';
import type { AssetInfo } from './utils';

interface PhotoGridState {
    selectedAsset?: AssetInfo;
    assets?: AssetInfo[];
    isZoomModalOpen?: boolean;
}

interface LocalState<T> {
    isLoading: boolean;
    data: T;
}

const initialState: PhotoGridState = {
    assets: [],
    isZoomModalOpen: false,
};

export function usePhotoGridStore(): LocalState<PhotoGridState | undefined> {
    const productId = usePhotoGridProductId();
    const queryKey = usePhotoGridQueryKey();
    const { assets: serverAssets } = useSSRProduct();
    const queryClient = useQueryClient();

    const isBrandSpecialistVideosEnabled = useCustomPreferenceOrDefault(
        CustomPreference.BRAND_SPECIALIST_VIDEO_ENABLED,
        false,
    );

    const { data: product, isFetching: isFetchingProduct } = useProduct(productId);

    const { data, isLoading: isLoadingAssets } = useQuery<PhotoGridState>({
        queryFn: async () => {
            const assets = product
                ? await buildAssets(product, { isBrandSpecialistVideosEnabled })
                : [];

            const state = queryClient.getQueryData<PhotoGridState>(queryKey);

            return {
                ...(state ?? initialState),
                assets,
                selectedAsset: state?.selectedAsset ?? assets[0],
            };
        },
        staleTime: 0,
        placeholderData: {
            ...initialState,
            assets: serverAssets,
            selectedAsset: serverAssets?.length ? serverAssets[0] : undefined,
        },
        keepPreviousData: true, // Don't change the data to undefined while loading.
        enabled: !!product && product.id === productId,
        queryKey,
    });

    return { isLoading: isFetchingProduct || isLoadingAssets, data };
}

export function usePhotoGridStoreMutation(): UseMutationResult<
    PhotoGridState,
    unknown,
    Partial<PhotoGridState>
> & {
    nextAsset: () => void;
    previousAsset: () => void;
} {
    const queryClient = useQueryClient();
    const queryKey = usePhotoGridQueryKey();

    const mutation = useMutation<PhotoGridState, unknown, Partial<PhotoGridState>>({
        mutationFn: (value) => Promise.resolve(value),
        onSuccess: (value) => {
            queryClient.setQueryData<PhotoGridState>(queryKey, (oldData) => ({
                ...oldData,
                ...value,
            }));
        },
    });

    const nextAsset = useCallback(() => {
        const state = queryClient.getQueryData<PhotoGridState>(queryKey);
        const assets = state?.assets ?? [];
        const selectedAsset = state?.selectedAsset;

        if (selectedAsset) {
            const index = assets.findIndex((asset) => asset.id === selectedAsset.id);
            const nextIndex = assets.length > index + 1 ? index + 1 : 0;
            mutation.mutate({ selectedAsset: assets[nextIndex] });
        }
    }, [queryClient, mutation, queryKey]);

    const previousAsset = useCallback(() => {
        const state = queryClient.getQueryData<PhotoGridState>(queryKey);
        const assets = state?.assets ?? [];
        const selectedAsset = state?.selectedAsset;

        if (selectedAsset) {
            const index = assets.findIndex((asset) => asset.id === selectedAsset.id);
            const previousIndex = index > 0 ? index - 1 : assets.length - 1;
            mutation.mutate({ selectedAsset: assets[previousIndex] });
        }
    }, [queryClient, mutation, queryKey]);

    return {
        ...mutation,
        nextAsset,
        previousAsset,
    };
}
